
import { DtoKeys, Enums } from '@bode-canada/core';
import Typography from '@/ui-kit/components/typography';
import { TypographyProperty, TypographyType } from '@/ui-kit/components/typography/enums';
import {
  StyleColorSchemeType,
  StyleFontWeightType,
  StylePositionType,
  StyleSizeType,
} from '@/ui-kit/styles/enums';
import { ButtonFillType, ButtonProperty, ButtonPropsEventType } from '@/ui-kit/components/button/enums';
import { GutterProperty } from '@/ui-kit/components/gutter/enums';
import { ControlType } from '@/ui-kit/components/form/controls/enums';
import { LayoutItemColumn, LayoutItemProperty } from '@/ui-kit/components/layout-grid/item/enums';
import { LayoutContainerProperty } from '@/ui-kit/components/layout-grid/container/enums';
import LayoutGridContainer from '@/ui-kit/components/layout-grid/container';
import LayoutGridItem from '@/ui-kit/components/layout-grid/item';
import createMarketDataChartForm, {
  MarketDataChartFormControlName,
} from '@/components/features/market-data/components/chart/MarketDataChartControls';
import Form from '@/ui-kit/components/form';
import FormField from '@/ui-kit/components/form/form-field';
import { PROVINCES } from '@/constants';
import Gutter from '@/ui-kit/components/gutter';
import Button from '@/ui-kit/components/button';
import { SelectProperty } from '@/ui-kit/components/form/controls/components/select/enums';
import { InputFillType } from '@/ui-kit/components/form/controls/shared/input/enums';
import { CHART_LABEL } from '@/components/page-specific/market-data/constants';
import { CHART_SOLD_YEARS } from '@/constants/MarketResearchConstants';
import { DEFAULT_DATE_PERIOD } from '@/components/features/sold-data/shared/components/forms/date-period/constants';
import {
  MarketDataFilterFormControlName,
} from '@/components/features/market-data/components/filters/MarketDataFiltersFormControls';
import getMarketDataChartOptions from '@/components/features/market-data/components/chart/getMarketDataChartOptions';
import { track } from '@/utils/analytics';
import { MarketDataEvent } from '@/constants/AnalyticEvents';
import DashboardApiService from '@/services/api/serverless/dashboard';
import FilterMapper from '@/components/page-specific/market-data/mappers/filters';
import MarketDataApiService from '@/services/api/serverless/market-data';
import { FormFieldProperty } from '@/ui-kit/components/form/enums';
import OptionsHelper from '@/utils/OptionsHelper';
import Chart from '@/components/shared/chart/Chart';
import DownloadChartButton from '@/components/shared/chart/DownloadChartButton';
import ShareChartButton from '@/components/shared/chart/ShareChartButton';

export default {
  name: 'MarketDataChart',
  components: {
    ShareChartButton,
    DownloadChartButton,
    Chart,
    Typography,
    Form,
    LayoutGridContainer,
    LayoutGridItem,
    FormField,
    Gutter,
    Button,
  },
  props: {
    summaryParams: {
      type: Object,
      default: null,
    },
    province: {
      type: String,
      default: PROVINCES.AB,
    },
    initialChartType: {
      type: String,
      default: Enums.ChartTypeEnum.SOLD_PRICE,
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: 'Sold property trends',
    },
  },
  data() {
    return {
      formGroup: createMarketDataChartForm({
        [MarketDataChartFormControlName.ChartType]: this.initialChartType,
      }),
      chartData: null,
      loadingGraph: false,

      TypographyProperty,
      TypographyType,
      StyleFontWeightType,
      LayoutContainerProperty,
      StyleSizeType,
      LayoutItemProperty,
      LayoutItemColumn,
      ControlType,
      MarketDataChartFormControlName,
      StylePositionType,
      GutterProperty,
      ButtonProperty,
      ButtonPropsEventType,
      ButtonFillType,
      StyleColorSchemeType,
      FormFieldProperty,
      MarketDataEvent,
    };
  },
  computed: {
    chartTypeValue() {
      return this.formGroup.getControl(MarketDataChartFormControlName.ChartType).value;
    },
    chartTypeInputProps() {
      return {
        [SelectProperty.Name]: MarketDataChartFormControlName.ChartType,
        [SelectProperty.FillType]: InputFillType.Underlined,
        [SelectProperty.Label]: 'Chart Type',
        [SelectProperty.Options]: OptionsHelper.mapTextAndValueIntoOptions(
          Object.entries(CHART_LABEL)
            .filter(([value]) => value !== Enums.ChartTypeEnum.WEEKLY_VIEWS)
            .map(([value, text]) => ({ value, text })),
        ),
      };
    },

    transactionDateControl() {
      return this.formGroup.getControl(MarketDataChartFormControlName.TransactionDate);
    },
    transactionDate() {
      return this.transactionDateControl.value;
    },
    transactionDateInputProps() {
      return {
        [SelectProperty.Name]: MarketDataChartFormControlName.TransactionDate,
        [SelectProperty.FillType]: InputFillType.Underlined,
        [SelectProperty.Label]: 'Time Range',
        [SelectProperty.Options]: OptionsHelper.mapValuesIntoOptions(CHART_SOLD_YEARS),
      };
    },
    chartName() {
      return CHART_LABEL[this.chartTypeValue];
    },
    chartOptions() {
      return getMarketDataChartOptions(this.chartData.chartType, this.chartData.timeline);
    },
    chartSeries() {
      return this.chartData.charts;
    },
    chartParams() {
      return {
        ...this.summaryParams,
        transactionDate: this.transactionDate,
      };
    },
  },
  watch: {
    summaryParams: {
      immediate: true,
      async handler(value) {
        // NOTE: charts are only rendered on client, so fetch only on client
        if (process.client && value) {
          const newTransactionDate = value[MarketDataFilterFormControlName.TransactionDate] || DEFAULT_DATE_PERIOD;
          this.transactionDateControl.setValue(newTransactionDate);

          await this.getChartData(this.chartTypeValue, this.chartParams);
        }
      },
    },
  },
  methods: {
    async handleSubmit() {
      track(MarketDataEvent.GenerateGraphClick, {
        chartType: this.chartTypeValue,
        timeRange: this.transactionDate,
      });

      await this.getChartData(this.chartTypeValue, this.chartParams);
    },

    async getChartData(chartType, params) {
      this.loadingGraph = true;

      try {
        const chartData = chartType === Enums.ChartTypeEnum.WEEKLY_VIEWS
          ? await DashboardApiService.getListingViewsByListingId(params.listingId)
          : await this.fetchMarketChartData(chartType, params);

        this.chartData = {
          ...chartData,
          chartType,
        };
      } catch {
        // ignore
      } finally {
        this.loadingGraph = false;
      }
    },

    async fetchMarketChartData(chartType, requestFilter) {
      const { closedAt, ...filters } = FilterMapper.mapToDto(requestFilter);
      const timeStampKey = chartType === Enums.ChartTypeEnum.NUMBER_OF_NEW_LISTINGS
        ? DtoKeys.SearchListingFilterDtoKey.PUBLISHED_AT
        : DtoKeys.SearchListingFilterDtoKey.CLOSED_AT;
      const chartRequestBody = {
        filters: {
          ...filters,
          [timeStampKey]: closedAt,
        },
        chartType,
      };
      return MarketDataApiService.getChartData(chartRequestBody);
    },
  },
};
